import type {ChatEntry, ChatResponse, EntryContent} from './chatTypes';

/** The client owns display history; the server owns which actions remain available. */
export function createTranscriptState() {
    let nextId = 1;

    function append(entries: ChatEntry[], content: EntryContent): ChatEntry {
        const entry = {...content, id: nextId++, stale: false};
        entries.push(entry);
        return entry;
    }

    function apply(entries: ChatEntry[], response: ChatResponse): void {
        // Every turn renders its own controls; an earlier copy is never re-activated in place,
        // it only goes stale below once a newer entry holds its action.
        for (const content of response.entries) append(entries, content);
        const available = new Set(response.activeActions);
        for (const entry of [...entries].reverse()) {
            if (!entry.action) continue;
            entry.stale = !available.delete(entry.action);
        }
    }

    function disable(entries: ChatEntry[]): number[] {
        const ids: number[] = [];
        for (const entry of entries) {
            if (entry.action && !entry.stale) {
                ids.push(entry.id);
                entry.stale = true;
            }
        }
        return ids;
    }

    function restore(entries: ChatEntry[], ids: number[]): void {
        for (const entry of entries) {
            if (ids.includes(entry.id)) entry.stale = false;
        }
    }

    return {append, apply, disable, restore, nextId: () => nextId};
}
